import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GraduationCap, Building2, Users, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import Logo from "../../components/shared/Logo";

const roles = [
  {
    id: "learner",
    title: "Learner",
    description: "Access courses, track progress, and earn certificates",
    icon: GraduationCap,
    color: "bg-primary/10",
    iconColor: "text-primary",
    borderColor: "border-primary"
  },
  {
    id: "tenant_admin",
    title: "Enterprise Admin",
    description: "Deploy and manage training for your organization",
    icon: Building2,
    color: "bg-accent/10",
    iconColor: "text-accent",
    borderColor: "border-accent"
  }
];

const RoleSelectPage = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const navigate = useNavigate();

  const handleContinue = () => {
    if (!selectedRole) return;
    
    // Store selected role
    const signupData = JSON.parse(sessionStorage.getItem("signup_data") || "{}");
    sessionStorage.setItem("signup_data", JSON.stringify({ ...signupData, role: selectedRole }));
    
    // If enterprise admin, go to onboarding wizard
    if (selectedRole === "tenant_admin") {
      navigate("/auth/onboarding");
    } else {
      // For learners, go to workspace selection
      navigate("/auth/workspace");
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl floating" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl floating-delayed" />
      </div>

      <div className="w-full max-w-2xl relative z-10 animate-fade-in">
        {/* Back Button */}
        <Link
          to="/auth/signup"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to signup
        </Link>

        <div className="glass-card p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              How will you use Odesie?
            </h1>
            <p className="text-muted-foreground">Select your role to customize your experience</p>
          </div>

          {/* Role Options */}
          <div className="space-y-4 mb-8">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`w-full p-5 rounded-xl border-2 transition-all duration-200 text-left ${
                  selectedRole === role.id
                    ? `${role.borderColor} ${role.color}`
                    : "border-border hover:border-muted-foreground/30 bg-background/50"
                }`}
                data-testid={`role-option-${role.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${role.color} flex items-center justify-center shrink-0`}>
                    <role.icon className={`w-6 h-6 ${role.iconColor}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
                      {role.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{role.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 ${
                    selectedRole === role.id ? role.borderColor : "border-border"
                  }`}>
                    {selectedRole === role.id && (
                      <div className={`w-2.5 h-2.5 rounded-full ${role.iconColor.replace('text-', 'bg-')}`} />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Continue Button */}
          <Button 
            onClick={handleContinue}
            disabled={!selectedRole}
            className="w-full h-12 text-base font-semibold btn-primary"
            data-testid="role-continue-btn"
          >
            Continue
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          {/* Login Link */}
          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/auth/login" className="text-primary font-medium hover:text-primary/80 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectPage;
